import React from "react";
import {
  Subheading,
  Paragraph,
  Highlight,
  List,
  ListItem,
  Link
} from "./components";

export const content = {
  heading: "About DynaMed Decisions",
  sections: [
    {
      id: "mission-and-purpose",
      label: "Mission and Purpose",
      content: (
        <>
          <Paragraph>
            DynaMed Decisions is a clinical decision support solution that helps
            you deliver personalized, evidence-based care efficiently. Backed by
            EBSCO’s rigorous evidence-based methodology, our tools reduce
            cognitive burden, support meaningful patient engagement, and guide
            shared decision-making at the point of care. Developed by experts
            and continuously updated, DynaMed Decisions offers risk calculators
            and treatment guidance across 30+ specialties – personalized to the
            patient and grounded in the latest evidence.
          </Paragraph>
        </>
      )
    },
    {
      id: "editorial-and-ebm-policies",
      label: "Editorial and EBM Policies",
      content: (
        <>
          <Subheading>Evidence-Based Methodology (EBM)</Subheading>
          <Paragraph>
            EBSCO’s evidence-based methodology for clinical solutions is the
            foundation underlying all of DynaMed Decisions’ content. We
            continuously and systematically identify, summarize, interpret, and
            synthesize the best-available literature and adjust conclusions and
            guidance based on this dynamic, ever-growing evidence ecosystem,
            coupled with clinical expertise and incorporation of patient values
            and preferences whenever relevant. Details on our evidence-based
            methodology can be found{" "}
            <Link
              inline
              href="https://www.ebsco.com/clinical-decisions/dynamed-solutions/about/evidence-based-process/methodology"
            >
              here
            </Link>
            .
          </Paragraph>

          <Subheading>Editorial Process</Subheading>
          <Paragraph>
            DynaMed Decisions’ editorial policy is to provide clinicians with
            evidence-based, interactive tools to use at the point of care. The
            tool development process includes over 30 steps. An abbreviated
            overview of the tool development process is provided below:
          </Paragraph>
          <List>
            <ListItem>
              <Highlight bold>Step 1: Prioritization</Highlight> – DynaMed
              Decisions leadership identifies tool needs. When prioritizing
              topics, we consider internal and external requests, topics that
              address top health concerns, and topics that build out more
              cohesive specialty suites.
            </ListItem>
            <ListItem>
              <Highlight bold>Step 2: Scoping</Highlight> – The topic then
              undergoes a scoping process to identify the tool purpose,
              eligibility criteria, and components of the tool, such as clinical
              calculation or decision pathway. Scoping also includes a dedicated
              effort to delineate any complexities or challenges in the existing
              literature and identify how those things will be addressed when
              creating the tool.
            </ListItem>
            <ListItem>
              <Highlight bold>Step 3: Evidence search and analysis</Highlight> –
              In this next step, we identify relevant evidence, including
              clinical practice guidelines and peer-reviewed literature. We have
              partnered with McMaster University to develop systematic
              literature searches for our tools within a proprietary system.
              This system allows us to search content in DynaMedex and to
              conduct supplemental searches within PubMed. Evidence is
              critically appraised and synthesized using GRADE methods.
            </ListItem>
            <ListItem>
              <Highlight bold>Step 4: Build and review tool</Highlight> – The
              draft tool is built and reviewed internally. Our clinical decision
              support (CDS) tools go through several rounds of “functional
              review” to ensure the tool is working as intended. Our shared
              decision-making (SDM) tools are evaluated to ensure we meet plain
              language principles and criteria for the International Patient
              Decision Aid Standards (IPDAS).
            </ListItem>
            <ListItem>
              <Highlight bold>Step 5: External review</Highlight> – The draft
              tool is sent to external reviewers, including a generalist and
              specialist in the topic area, for review of clinical accuracy and
              relevance.
            </ListItem>
            <ListItem>
              <Highlight bold>Step 6: Editorial team review</Highlight> –
              External review feedback is resolved, and the tool is reviewed
              again for overall style and functionality. Additionally, for our
              CDS tools, any calculations performed within the tool are
              confirmed through a quality control process.
            </ListItem>
            <ListItem>
              <Highlight bold>Step 7: Testing and approval</Highlight> – In this
              step, CDS tools undergo final approval by DynaMed Decisions
              leadership, copyedit, and programmatic, end-to-end testing prior
              to being released. SDM tools undergo testing for readability via a
              marketing platform. Edits are incorporated, as needed, based on
              user feedback and then the SDM tool undergoes final approval by
              leadership and copyedit prior to being released to product.
            </ListItem>
          </List>

          <Subheading>Statement on AI Transparency</Subheading>
          <Paragraph>
            DynaMed Decisions considers transparency with regard to the use of
            artificial intelligence to be of the utmost importance. DynaMed
            Decisions does not use AI and utilizes only predictive models that
            have been published in peer-reviewed literature in any of its
            tooling. (Any predictive model used in a DynaMed Decisions tool is
            noted in the References section and is also clearly indicated both
            in the frontmatter of the tool and in the “Assessment” section where
            the results of the predictive model are displayed.)
          </Paragraph>
          <Paragraph>
            DynaMed Decisions tools and their output are not included in any
            responses presented by Dyna AI (the AI tool embedded in and solely
            available as an add-on within DynaMed, DynaMedex and Dynamic
            Health). This aligns with the Office of the National Coordinator for
            Health Information Technology (ONC) HTI-1 final rule, which
            establishes transparency requirements surrounding use of AI and
            other predictive algorithms.
          </Paragraph>

          <Subheading>Health Equity and Inclusion</Subheading>
          <Paragraph>
            DynaMed Decisions aligns with DynaMedex’s policies on health equity
            and inclusion to provide the most helpful information to healthcare
            professionals at the point of care to improve health outcomes
            worldwide regardless of race, ethnicity, sex, age, national origin
            and/or culture, socioeconomics, mental or physical abilities, sexual
            orientation, or gender identity and expression. DynaMed Decisions
            has implemented procedures for handling race and ethnicity, and sex
            and gender in tools, and we manage these constructs on a
            tool-by-tool basis to promote inclusivity.
          </Paragraph>

          <Subheading>Conflict of Interest Policy</Subheading>
          <Paragraph>
            All DynaMed Decisions editors and reviewers have declared no
            conflicts of interest. All individuals involved in editorial work
            are required to disclose financial conflicts of interest. Any
            potential conflicts of interest are reviewed and managed according
            to DynaMed LLC’s Disclosure of Competing Interests Policy, more
            about which can be found{" "}
            <Link
              inline
              href="https://www.ebsco.com/clinical-decisions/dynamed-solutions/about/conflict-of-interest"
            >
              here
            </Link>
            . Development of DynaMed Decisions tools is fully supported by EBSCO
            with no external funding.
          </Paragraph>
        </>
      )
    }
  ]
};
